import * as React from 'react';

export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  padding?: 'none' | 'sm' | 'md' | 'lg';
  hover?: boolean;
}

export interface CardHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  title: string;
  subtitle?: React.ReactNode;
  action?: React.ReactNode;
  borderBottom?: boolean;
}

export interface CardContentProps extends React.HTMLAttributes<HTMLDivElement> {
  noPadding?: boolean;
}

export interface CardFooterProps extends React.HTMLAttributes<HTMLDivElement> {
  borderTop?: boolean;
  alignment?: 'left' | 'center' | 'right' | 'between';
}

/**
 * Standardized Card Component
 * 
 * Design System Specifications:
 * - Background: White (#ffffff)
 * - Border: Light gray (#e9e9e9)
 * - Border radius: 10px
 * - Subtle shadow for depth
 * 
 * Usage:
 * ```tsx
 * <Card>
 *   <CardHeader title="Dashboard" subtitle="Overview" />
 *   <CardContent>Content here</CardContent>
 *   <CardFooter>Footer actions</CardFooter>
 * </Card>
 * ```
 */
export function Card({ 
  padding = 'none', 
  hover = false, 
  children, 
  className = '', 
  ...props 
}: CardProps) {
  const paddingStyles = {
    none: '',
    sm: 'p-4',
    md: 'p-6',
    lg: 'p-8',
  };
  
  const hoverStyles = hover ? 'hover:shadow-md transition-shadow cursor-pointer' : '';
  
  const combinedClassName = `bg-white rounded-[10px] border border-[#e9e9e9] ${paddingStyles[padding]} ${hoverStyles} ${className}`.trim();
  
  return (
    <div className={combinedClassName} {...props}>
      {children}
    </div>
  );
}

/**
 * CardHeader - Header section for cards
 */
export function CardHeader({ 
  title, 
  subtitle, 
  action, 
  borderBottom = false, 
  className = '',
  ...props 
}: CardHeaderProps) {
  const borderClass = borderBottom ? 'border-b border-[#e9e9e9]' : '';
  const combinedClassName = `px-6 py-5 ${borderClass} ${className}`.trim();
  
  return (
    <div className={combinedClassName} {...props}>
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <h2 className="text-[#1c1c1c] text-[20px] font-medium mb-1">{title}</h2>
          {subtitle && <p className="text-[#6b6b6b] text-[14px]">{subtitle}</p>}
        </div>
        {action && <div className="ml-4 shrink-0">{action}</div>}
      </div>
    </div>
  );
}

/**
 * CardContent - Main content area
 */
export function CardContent({ 
  noPadding = false, 
  children, 
  className = '', 
  ...props 
}: CardContentProps) {
  const paddingClass = noPadding ? '' : 'p-6';
  const combinedClassName = `${paddingClass} ${className}`.trim();
  
  return (
    <div className={combinedClassName} {...props}>
      {children}
    </div>
  );
}

/**
 * CardFooter - Footer section with actions
 */
export function CardFooter({ 
  borderTop = false, 
  alignment = 'right',
  children, 
  className = '', 
  ...props 
}: CardFooterProps) {
  const borderClass = borderTop ? 'border-t border-[#e9e9e9]' : '';
  
  const alignmentStyles = {
    left: 'justify-start',
    center: 'justify-center',
    right: 'justify-end',
    between: 'justify-between',
  };
  
  const combinedClassName = `px-6 py-4 flex items-center gap-3 ${borderClass} ${alignmentStyles[alignment]} ${className}`.trim();
  
  return (
    <div className={combinedClassName} {...props}>
      {children}
    </div>
  );
}
